import { useSelector } from 'react-redux';
import { deviceType } from 'react-device-detect';
import { useLocation } from 'react-router';
import { useEffect, useState } from 'react';
import { selectSystemLanguage } from '../redux/modules/data/localizationSlice';
import { selectBusinessArea, selectFavoriteList, selectFolder1, selectFolder2, selectFolder3, selectNews, selectProperty } from '../redux/modules/appSlice';
import useTranslation from '../hooks/useTranslations';
import { selectLastViewedObject } from '../redux/modules/data/objectsSlice';
import { selectMe } from '../redux/modules/data/userSlice';
import { selectSearchResultsCount, selectSearchString } from '../redux/modules/searchSlice';

const Tracking = () => {
	const t = useTranslation(true);

	const [trackingObject, setTrackingObject] = useState({});

	const systemLanguage = useSelector((state) => selectSystemLanguage(state));
	const location = useLocation();

	const businessArea = useSelector((state) => selectBusinessArea(state));
	const folder1 = useSelector((state) => selectFolder1(state));
	const folder2 = useSelector((state) => selectFolder2(state));
	const folder3 = useSelector((state) => selectFolder3(state));
	const property = useSelector((state) => selectProperty(state));
	const news = useSelector((state) => selectNews(state));
	const favoriteList = useSelector((state) => selectFavoriteList(state));
	const lastViewedObject = useSelector((state) => selectLastViewedObject(state));
	const user = useSelector((state) => selectMe(state));
	const searchString = useSelector((state) => selectSearchString(state));
	const searchResultsCount = useSelector((state) => selectSearchResultsCount(state));

	useEffect(() => {
		window.OLY.trackingObject = trackingObject;
	}, [trackingObject]);

	useEffect(() => {
		if (searchString && searchString !== '') {
			setTrackingObject((trackingObj) => ({
				...trackingObj,
				search: {
					term: searchString,
					numberOfResults: searchResultsCount,
				},
			}));
		}
	/* eslint-disable-next-line */
	}, [searchString, searchResultsCount]);

	useEffect(() => {
		if (lastViewedObject) {
			let lastViewedObjectId = '';
			let lastViewedObjectTitle = '';
			let activeBusinessAreaTitle = '';
			let organisationTitle = '';

			if (businessArea?.translationkey) {
				activeBusinessAreaTitle = t(businessArea.translationkey);
			}

			if (user?.organisation_name) {
				organisationTitle = user?.organisation_name;
			}

			if (lastViewedObject?.id) {
				lastViewedObjectId = lastViewedObject.id;
			}

			const tempTitle = [];
			if (lastViewedObject?.title1) {
				tempTitle.push(lastViewedObject.title1);
			}
			if (lastViewedObject?.title2) {
				tempTitle.push(lastViewedObject.title2);
			}
			if (tempTitle.length > 0) {
				lastViewedObjectTitle = tempTitle.join(' ');
			}

			setTrackingObject((trackingObj) => ({
				...trackingObj,
				assetView: {
					id: lastViewedObjectId,
					title: lastViewedObjectTitle,
					bu: activeBusinessAreaTitle,
					orga: organisationTitle,
				},
			}));
		}
	/* eslint-disable-next-line */
	}, [lastViewedObject]);

	useEffect(() => {
		setTrackingObject((trackingObj) => ({
			...trackingObj,
			general: {
				country: process.env.REACT_APP_TRACKING_COUNTRY,
				language: systemLanguage,
				currency: '',
				environment: process.env.NODE_ENV === 'development' ? 'DEV' : 'PROD',
				device: deviceType,
				type: 'WEBSITE',
				channel: process.env.REACT_APP_TRACKING_CHANNEL,
				division: 'MSD',
			},
		}));
	}, [systemLanguage]);

	useEffect(() => {
		const hierarchy = [];
		let currentPage = null;

		if (location.pathname.includes('/login')) {
			hierarchy.push({ id: 'login', name: t('login_login_button') });
			currentPage = { id: 'login', title: t('login_login_button'), name: t('login_login_button') };
		} else if (location.pathname.includes('/forgot-password')) {
			hierarchy.push({ id: 'forgot-password', name: t('webapp_forgot_activationkey') });
			currentPage = { id: 'forgot-password', title: t('webapp_forgot_activationkey'), name: t('webapp_forgot_activationkey') };
		} else if (location.pathname.includes('/register')) {
			hierarchy.push({ id: 'register', name: t('register_register_button') });
			currentPage = { id: 'register', title: t('register_register_button'), name: t('register_register_button') };
		} else if (location.pathname.includes('/')) {
			hierarchy.push({ id: 'home', name: t('tab_main') });
			currentPage = { id: 'home', title: t('tab_main'), name: t('tab_main') };
		}

		if (location.pathname.includes('/search')) {
			hierarchy.push({ id: 'search', name: t('general_search') });
			currentPage = { id: 'search', title: t('general_search'), name: t('general_search') };
		}

		if (location.pathname.includes('/news')) {
			hierarchy.push({ id: 'newsList', name: t('news_button') });
			currentPage = { id: 'newsList', title: t('news_button'), name: t('news_button') };
		}

		if (location.pathname.includes('/recently-viewed')) {
			hierarchy.push({ id: 'recently-viewed', name: t('dashboard_recently_used_button') });
			currentPage = { id: 'recently-viewed', title: t('dashboard_recently_used_button'), name: t('dashboard_recently_used_button') };
		}

		if (location.pathname.includes('/favorites')) {
			hierarchy.push({ id: 'favorites', name: t('tab_favorite_lists') });
			currentPage = { id: 'favorites', title: t('tab_favorite_lists'), name: t('tab_favorite_lists') };
		}

		if (location.pathname.includes('/evaluations')) {
			hierarchy.push({ id: 'evaluations', name: t('eval_app_evaluations') });
			currentPage = { id: 'evaluations', title: t('eval_app_evaluations'), name: t('eval_app_evaluations') };
		}

		if (news) {
			hierarchy.push({ id: `news-${news.id}`, name: news.title });
			currentPage = { id: `news-${news.id}`, title: news.title, name: news.title };
		}

		if (favoriteList) {
			hierarchy.push({ id: `favoriteList-${favoriteList.id}`, name: favoriteList.title });
			currentPage = { id: `favoriteList-${favoriteList.id}`, title: favoriteList.title, name: favoriteList.title };
		}

		if (businessArea) {
			hierarchy.push({ id: `ba-${businessArea.id}`, name: t(businessArea.translationkey) });
			currentPage = { id: `ba-${businessArea.id}`, title: t(businessArea.translationkey), name: t(businessArea.translationkey) };
		}

		if (folder1) {
			hierarchy.push({ id: `folder-${folder1.id}`, name: t(folder1.translationkey) });
			currentPage = { id: `folder-${folder1.id}`, title: t(folder1.translationkey), name: t(folder1.translationkey) };
		}

		if (folder2) {
			hierarchy.push({ id: `folder-${folder2.id}`, name: t(folder2.translationkey) });
			currentPage = { id: `folder-${folder2.id}`, title: t(folder2.translationkey), name: t(folder2.translationkey) };
		}

		if (folder3) {
			hierarchy.push({ id: `folder-${folder3.id}`, name: t(folder3.translationkey) });
			currentPage = { id: `folder-${folder3.id}`, title: t(folder3.translationkey), name: t(folder3.translationkey) };
		}

		if (property) {
			hierarchy.push({ id: `property-${property.id}`, name: t(property.translationkey) });
			currentPage = { id: `property-${property.id}`, title: t(property.translationkey), name: t(property.translationkey) };
		}

		const hierarchyName = hierarchy.map((entry) => entry.name).join(' || ');

		setTrackingObject((trackingObj) => ({
			...trackingObj,
			page: {
				id: currentPage?.id,
				title: currentPage?.title,
				name: currentPage?.name,
				hierarchy,
				hierarchyName,
				statusCode: 200,
			},
		}));
	/* eslint-disable-next-line */
	}, [businessArea, folder1, folder2, folder3, property, favoriteList, news, location.pathname]);

	return null;
};

export default Tracking;
